// this is for adding and editing the menu
import React, { useState } from "react";
import Drawer from "@mui/material/Drawer";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { MENU_ITEM_STATUS, stripBlankMenuNumbers } from "../menu.utils";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_MENU,
  UPDATE_MENU,
  CREATE_ADDON_TYPE,
  CREATE_UPDATE_ADDON,
  UPLOAD_MENU_ITEM_IMAGE,
} from "../../../Graphql/mutations";
import { LIST_ADDON_TYPE } from "../../../Graphql/queries";
// MAIN-848: fast client-side checks + base64 encoding for the hardened upload.
import {
  fileToBase64,
  resolveSubmittedItemImage,
  validateSelectedImageFile,
} from "./menuItemImage.utils";
import {
  Button,
  CircularProgress,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { toggleSnackbar, authVar, userDeatils } from "../../../ReactiveVariables/index";
import Avatar from "@mui/material/Avatar";
import CropImage from "../../Common/cropImage";
import { generateDupeAddons, generateDupeAddOnsList } from "../../../lib/util";
import imageCompression from "browser-image-compression";
import { postiveValueConverter } from "../../Common/commonUtils";
import { useNavigate } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";

const Dietary = [
  "Vegetarian",
  "Non-Vegetarian",
  "Gluten Free",
  "Veg and Gluten Free",
  "Non-Veg and Gluten Free",
  "None",
];

export function AddMenu(props) {
  const navigate = useNavigate();
  const user = useReactiveVar(userDeatils);
  const [isImageDelete, setIsImageDelete] = useState(false);
  const [imageDeleteCheck, setImageDeleteCheck] = useState(false);
  const [checked, setChecked] = useState(
    props?.selectedItem?.offerIndPkdFlg
      ? props?.selectedItem?.offerIndPkdFlg
      : props?.data?.getOneMenu?.offerIndPkdFlg
      ? props?.data?.getOneMenu?.offerIndPkdFlg
      : false,
  );
  const [popular, setPopular] = useState(
    props?.selectedItem?.markasPopularFlg
      ? props?.selectedItem?.markasPopularFlg
      : props?.data?.getOneMenu?.markasPopularFlg
      ? props?.data?.getOneMenu?.markasPopularFlg
      : false,
  );
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const modifyTitle = props?.isDupe ? "Duplicate" : props?.selectedItem ? "Edit" : "Add";
  // MAIN-844: a Draft is allowed to be half-finished, so the fields that only
  // matter for an *orderable* item are required just for Publish. Item name and
  // category stay required either way: without them a draft cannot be found or
  // managed in the editor.
  const requiredWhenPublishing = (field, message) =>
    field.when("status", {
      is: (value) => value !== MENU_ITEM_STATUS.DRAFT,
      then: (rule) => rule.required(message),
      otherwise: (rule) => rule.notRequired(),
    });

  const schema = yup.object().shape({
    status: yup.string(),
    itemName: yup.string().required("Item Name is required"),
    categoryId: yup.string().required("Category is required"),
    // MAIN-844: the API's authoritative publish check (menu-publish-state.js,
    // validateMenuItemInput) never requires dietary — it only enforces
    // itemName and a valid price. Requiring it here would block a publish
    // the backend itself allows.
    dietory: yup.string(),
    // Not backend-enforced either, but a published item with no minQuantity
    // renders "undefined min" in this same list (index.js), so it stays
    // required to publish.
    minQuantity: requiredWhenPublishing(
      yup.string(),
      "Min Quantity is required",
    ),
    price: requiredWhenPublishing(
      yup.string(),
      "Price is required to publish this item",
    ),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      status:
        props?.selectedItem?.status ||
        props?.data?.getOneMenu?.status ||
        MENU_ITEM_STATUS.PUBLISHED,
    },
  });

  const [createMenu, { loading }] = useMutation(CREATE_MENU, {
    refetchQueries: ["ListMenu", "ListCategoryOrder", "ListAddOnType", "GetOneMenu"],
    onCompleted: (res) => {
      !imageDeleteCheck && props.setOpenAddMenu(false);
      !imageDeleteCheck &&
        props?.selectedItem === null &&
        props.setSelectedItem &&
        props.setSelectedItem(null);
    },
  });

  const userType =
    user?.data?.login?.userType ||
    user?.data?.currentUserDetails?.userType ||
    user?.data?.createUserIdentity?.userType ||
    user?.data?.createCaterer?.userType;
  const catererId = props?.selectedItem?.catererId;

  const [updateMenu, { loading: updateLoading }] = useMutation(UPDATE_MENU, {
    refetchQueries: ["ListMenu", "ListCategoryOrder"],
    onCompleted: (res) => {
      !imageDeleteCheck && props.setOpenAddMenu(false);
      !imageDeleteCheck && props.setSelectedItem && props.setSelectedItem(null);
    },
  });

  const { data: addOns } = useQuery(LIST_ADDON_TYPE, {
    variables: { catererId, menuId: props?.selectedItem?._id },
  });

  const [createAddOnType] = useMutation(CREATE_ADDON_TYPE);

  const [createupdateAddon] = useMutation(CREATE_UPDATE_ADDON, {
    refetchQueries: ["ListAddOnType"],
  });

  // MAIN-848: the image is uploaded through the API, which validates the
  // type/size, rejects corrupt files, resizes, strips metadata and stores it
  // under a caterer-scoped key it can later delete safely. The old path
  // handed a signed URL straight to the browser and PUT the raw file, so
  // none of that happened.
  const [uploadMenuItemImage] = useMutation(UPLOAD_MENU_ITEM_IMAGE);

  function handleUploadClick(imagefile) {
    // MAIN-848: fail fast in the browser on type/size before we base64 a
    // file the server would only reject. The server stays authoritative.
    const check = validateSelectedImageFile(imagefile);
    if (!check.ok) {
      toggleSnackbar({ status: true, message: check.message, variant: "error" });
      return;
    }
    const obj = {
      fileName: imagefile.name,
      url: URL.createObjectURL(imagefile),
      file: imagefile,
    };
    setSelectedImage(obj);
    // The upload itself now happens on submit, so a caterer who picks an
    // image and then cancels never leaves an orphaned object behind.
    setImageDeleteCheck(false);
    setIsImageDelete(false);
  }

  function checkImage(e) {
    let file = e.target.files[0];
    if (!(file?.type == "image/jpeg" || file?.type == "image/png" || file?.type == "image/jpg")) {
      toggleSnackbar({
        status: true,
        message: "File does not support. You must use .png or .jpeg",
        variant: "error",
      });
      return false;
    } else if (file.size > 5242880) {
      toggleSnackbar({
        status: true,
        message: "Please upload a file smaller than 5 MB",
        variant: "error",
      });
      return false;
    } else {
      var options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 300,
        useWebWorker: true,
      };
      imageCompression(file, options)
        .then((compressedFile) => {
          setCropImage(compressedFile);
          setCropOpen(true);
        })
        .catch((err) => {
          const message = err && err.message;
          toggleSnackbar({
            status: true,
            message: message,
            variant: "error",
          });
        });
    }
  }

  // call upload after crop
  function handelCropDone(result) {
    setCropImage(null);
    handleUploadClick(result);
  }

  // MAIN-848: upload happens here, at submit, and the menu is only written
  // once the image is safely stored — so a failed upload never saves a menu
  // row pointing at an image that does not exist, and the API only deletes a
  // replaced image after the new one is persisted.
  async function uploadImage(params) {
    if (imageUploadLoading) return; // guard against a double submit
    let uploadedUrl = null;
    // MAIN-848: the server derives the image metadata while processing the
    // upload; carry it through so itemImageMeta is persisted alongside the URL.
    let uploadedMeta = null;

    if (selectedImage?.file) {
      setImageUploadLoading(true);
      try {
        const base64 = await fileToBase64(selectedImage.file);
        const res = await uploadMenuItemImage({
          variables: {
            catererId:
              props?.getLocId ||
              props?.selectedItem?.catererId ||
              props?.data?.getOneMenu?.catererId,
            file: base64,
            filename: selectedImage.fileName,
            mimetype: selectedImage.file.type,
          },
        });
        uploadedMeta = res?.data?.uploadMenuItemImage || null;
        uploadedUrl = uploadedMeta?.url || null;
        if (!uploadedUrl) throw new Error("The image could not be uploaded.");
      } catch (err) {
        setImageUploadLoading(false);
        toggleSnackbar({
          status: true,
          message: err?.message || "The image could not be uploaded.",
          variant: "error",
        });
        return; // do NOT save the menu item with a broken/missing image
      }
      setImageUploadLoading(false);
    }

    onSubmit(params, uploadedUrl, uploadedMeta);
  }

  // for allowing only positive values
  const handleInputChange = (event) => {
    event.target.value = postiveValueConverter(event, "withDecimal");
  };
  const handleInputChangeWithoutDecimal = (event) => {
    event.target.value = postiveValueConverter(event, "withoutDecimal");
  };

  function onSubmit(params, uploadedUrl = null, uploadedMeta = null) {
    const createMenuData = {
      // MAIN-844: strip blank optional numbers BEFORE spreading. The guard
      // below cannot help on its own, because `...params` would otherwise
      // reintroduce `price: ""` and fail the GraphQL Float coercion.
      ...stripBlankMenuNumbers(params),
      offerIndPkdFlg: checked,
      markasPopularFlg: popular,
      ...(params.additionalPkdPrice && parseFloat(params["additionalPkdPrice"])),
      // MAIN-844: a draft may legitimately have no price yet; sending NaN would
      // fail the Float coercion, so omit it instead.
      ...(params["price"] === "" ||
      params["price"] === null ||
      params["price"] === undefined
        ? {}
        : { price: parseFloat(params["price"]) }),
      catererId:
        props?.getLocId || props?.selectedItem?.catererId || props?.data?.getOneMenu?.catererId,
      // When duplicating, initilize itemImage as original.
      ...(props?.isDupe && {
        itemImage: props?.selectedItem?.itemImage ? props?.selectedItem?.itemImage : "",
      }),
      // MAIN-848: a freshly uploaded image wins; an explicit remove clears the
      // field; otherwise itemImage is omitted entirely so a partial update
      // never wipes an existing (including legacy) image it was not asked to
      // touch. The API deletes the replaced object only after this write.
      ...resolveSubmittedItemImage({ uploadedUrl, uploadedMeta, isImageDelete }),
      // Do not pass `_id` if is duplicate.
      ...((props?.selectedItem !== null || props?.data?.getOneMenu?._id) &&
        !props?.isDupe && {
          _id: props?.selectedItem?._id || props?.data?.getOneMenu?._id,
        }),
    };

    try {
      // If selected item is null or isDupe then create new item.
      if (props?.selectedItem === null || props?.isDupe) {
        return createMenu({ variables: { data: createMenuData } })
          .then((res) => {
            const addOnsToDupe = addOns?.listAddOnType?.data || [];

            // If duplicating menu, also duplicate add-ons if they exist.
            if (props?.isDupe && addOnsToDupe.length > 0) {
              const menuId = res?.data?.createMenu?._id;
              const addOnsToCreate = generateDupeAddons(menuId, addOnsToDupe);

              // Iterate through all add-ons to duplicate
              return Promise.all(
                addOnsToCreate.map((data) => createAddOnType({ variables: { data } })),
              )
                .then((allRes) => {
                  // Duplicate sub-ons if they exist
                  return allRes.map((addOnCreateRes) => {
                    const data = addOnCreateRes?.data?.createAddOnType;
                    const newAddOnId = data._id;
                    const newMenuId = data.menuId;

                    // Create add-ons list and sub add-ons
                    const listToDupe = generateDupeAddOnsList(addOnsToDupe, newAddOnId, newMenuId);

                    // Loop over newly generated AddonsList and create them
                    listToDupe.forEach((listItem) => {
                      createupdateAddon({ variables: { addOnTypeId: newAddOnId, data: listItem } });
                    });

                    return toggleSnackbar({
                      status: true,
                      message: "Menu Duplicated Successfully",
                      variant: "success",
                    });
                  });
                })
                .catch((err) => {
                  const message = err && err.message;

                  return toggleSnackbar({
                    status: true,
                    message: message,
                    variant: "error",
                  });
                });
            }

            return toggleSnackbar({
              status: true,
              message: "Menu Created Successfully",
              variant: "success",
            });
          })
          .catch((err) => {
            const message = err && err.message;

            if (message === "Not authenticated") {
              localStorage.clear();

              authVar({
                loaded: true,
                auth: false,
              });

              return userType === "Admin"
                ? navigate("/admin", { state: "noAuth" })
                : navigate("/login", { state: "noAuth" });
            }

            return toggleSnackbar({
              status: true,
              message: message,
              variant: "error",
            });
          });
      }

      // Edit
      return updateMenu({
        variables: {
          id: props?.selectedItem?._id || props?.data?.getOneMenu?._id,
          data: createMenuData,
        },
      })
        .then((res) => {
          toggleSnackbar({
            status: true,
            message: "Menu Updated successfully",
            variant: "success",
          });
        })
        .catch((err) => {
          const message = err && err.message;

          if (message === "Not authenticated") {
            localStorage.clear();

            authVar({
              loaded: true,
              auth: false,
            });

            return userType === "Admin"
              ? navigate("/admin", { state: "noAuth" })
              : navigate("/login", { state: "noAuth" });
          }

          return toggleSnackbar({
            status: true,
            message: message,
            variant: "error",
          });
        });
    } catch (err) {
      const message = err && err.message;

      toggleSnackbar({
        status: true,
        message: message,
        variant: "error",
      });
    }
  }

  function handleDeleteImage() {
    setImageDeleteCheck(true);
    setIsImageDelete(true);
    setSelectedImage(null);
  }

  return (
    <Drawer
      className="common-drawer"
      anchor={"right"}
      open={props.openAddMenu}
      onClose={() => {
        props.setOpenAddMenu(false);
        props.setSelectedItem && props.setSelectedItem(null);
      }}>
      <div className="drawer-head">
        <h4 className="h4">
          {modifyTitle} an item on{" "}
          {props?.menuItems?.listMenu?.catererData?.businessName ||
            props?.data?.getOneMenu?.catererDtls?.businessName}
          ,{" "}
          {props?.selectedItem?.catererDtls?.locationName ||
            props?.data?.getOneMenu?.catererDtls?.locationName ||
            props?.menuItems?.listMenu?.catererData?.locationName}
        </h4>
        <span
          style={{ cursor: "pointer" }}
          onClick={() => {
            props.setOpenAddMenu(false);
            props.setSelectedItem && props.setSelectedItem(null);
          }}>
          <img src="../assets/images/x-icon.svg" className="img-fluid" alt="close icon" />
        </span>
      </div>
      <form onSubmit={handleSubmit(uploadImage)} noValidate  autoComplete="off">
        <div className="create-step-form">
          <div className="container-fluid">
            {/*  crop image component */}
            <CropImage
              open={cropOpen}
              setOpen={setCropOpen}
              image={cropImage}
              handelCropDone={handelCropDone}
              aspectRatio={1}
            />
            {/* choose file */}
            <input
              type={"file"}
              className="d-none"
              id="add-user-input"
              accept=".jpg, .png, .jpeg"
              onChange={checkImage}
              onClick={(event) => {
                event.target.value = null;
              }}
            />

            <div className="text-center">
              <div className="upload-img-wrap">
                <Avatar
                  alt="Remy Sharp"
                  src={
                    selectedImage
                      ? selectedImage.url
                      : props?.selectedItem?.itemImage && !imageDeleteCheck
                      ? props?.selectedItem?.itemImage
                      : props?.data?.getOneMenu?.itemImage && !imageDeleteCheck
                      ? props?.data?.getOneMenu?.itemImage
                      : "../assets/images/uploadFood.jpg"
                  }
                  className="upload-img upload-img-square"
                  onClick={(e) => document.getElementById("add-user-input").click()}
                />
                {(selectedImage === null ||
                  props?.selectedItem?.itemImage === undefined ||
                  props?.data?.getOneMenu?.itemImage === null ||
                  (props?.data?.getOneMenu?.itemImage && imageDeleteCheck === true)) && (
                  <img
                    alt="uploadImage"
                    onClick={(e) => document.getElementById("add-user-input").click()}
                    src="../assets/images/icon-upload-plus.svg"
                    className="upload-icon img-fluid"
                  />
                )}
                {(selectedImage ||
                  (props?.selectedItem?.itemImage && imageDeleteCheck === false) ||
                  (props?.data?.getOneMenu?.itemImage && imageDeleteCheck === false)) && (
                  <img
                    alt="closeIconImage"
                    onClick={() => handleDeleteImage()}
                    src="../assets/images/close-black.svg"
                    className="upload-icon img-fluid"
                  />
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 textBox">
                <TextField
                  id="menu-item-name"
                  defaultValue={props?.selectedItem?.itemName || props?.data?.getOneMenu?.itemName}
                  label="Item Name*"
                  autoFocus
                  variant="outlined"
                  className="textField allFeild"
                  {...register("itemName")}
                />
                {errors.itemName && <span className="error-msg">{errors.itemName.message}</span>}
              </div>
              <div className="col-md-6 allFeild">
                <Box sx={{ minWidth: 200 }}>
                  <FormControl fullWidth>
                    <InputLabel id="menu-item-category-label">Category*</InputLabel>
                    <Select
                      labelId="menu-item-category-label"
                      id="menu-item-category"
                      defaultValue={
                        props?.selectedItem?.categoryId || props?.data?.getOneMenu?.categoryId || ""
                      }
                      label="Category"
                      {...register("categoryId")}>
                      {props?.categoryData?.listCategory?.data?.map((item) => (
                        <MenuItem key={item?._id} value={item?._id}>
                          {item?.categoryTitle}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {errors.categoryId && (
                  <span className="error-msg">{errors.categoryId.message}</span>
                )}
              </div>
              <div className="col-md-6 allFeild">
                <Box sx={{ minWidth: 200 }}>
                  <FormControl fullWidth>
                    <InputLabel id="menu-item-dietary-label">Dietary</InputLabel>
                    <Select
                      labelId="menu-item-dietary-label"
                      id="menu-item-dietary"
                      defaultValue={
                        props?.selectedItem?.dietory || props?.data?.getOneMenu?.dietory || ""
                      }
                      {...register("dietory")}
                      label="Dietory">
                      {Dietary.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {errors.dietory && <span className="error-msg">{errors.dietory.message}</span>}
              </div>
              <div className="col-md-12 textBox">
                <TextField
                  id="menu-item-description"
                  defaultValue={
                    props?.selectedItem?.itemDescription || props?.data?.getOneMenu?.itemDescription
                  }
                  label="Description"
                  multiline
                  variant="outlined"
                  className="textField allFeild"
                  {...register("itemDescription")}
                />
              </div>
              <div className="col-md-4 textBox">
                <Box sx={{ minWidth: 150 }}>
                  <FormControl fullWidth>
                    <InputLabel id="menu-item-type-label">Type</InputLabel>
                    <Select
                      labelId="menu-item-type-label"
                      id="menu-item-type"
                      defaultValue={
                        props?.selectedItem?.itemType || props?.data?.getOneMenu?.itemType || ""
                      }
                      {...register("itemType")}
                      label="Type">
                      <MenuItem value={"Single"}>Single</MenuItem>
                      <MenuItem value={"Tray"}>Tray</MenuItem>
                      <MenuItem value={"Packaged"}>Packaged</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </div>
              <div className="col-md-4 textBox">
                <TextField
                  id="menu-item-min-quantity"
                  defaultValue={
                    props?.selectedItem?.minQuantity || props?.data?.getOneMenu?.minQuantity
                  }
                  label="Min Quantity*"
                  // inputProps={{ type: "number" }}
                  variant="outlined"
                  className="textField allFeild"
                  {...register("minQuantity")}
                  onChange={handleInputChangeWithoutDecimal}
                />
                {errors.minQuantity && (
                  <span className="error-msg">{errors.minQuantity.message}</span>
                )}
              </div>
              <div className="col-md-4 textBox">
                <TextField
                  id="menu-item-price"
                  label="Price*"
                  // type={"number"}
                  defaultValue={props?.selectedItem?.price || props?.data?.getOneMenu?.price}
                  variant="outlined"
                  className="textField allFeild"
                  // inputProps={{ type: "number" }}
                  {...register("price")}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                {errors.price && <span className="error-msg">{errors.price.message}</span>}
              </div>
              {checked && (
                <div className="col-md-12 textBox">
                  <TextField
                    id="menu-item-additional-price"
                    label="Additional Price for Individual Packaging"
                    // type={"number"}
                    inputProps={{ type: "number", min: "0" }}
                    defaultValue={
                      props?.selectedItem?.additionalPkdPrice ||
                      props?.data?.getOneMenu?.additionalPkdPrice
                    }
                    variant="outlined"
                    className="textField allFeild"
                    {...register("additionalPkdPrice")}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="createStepBtn">
          <Button
            className="cancelBtn"
            variant="contained"
            color="secondary"
            onClick={() => {
              props.setOpenAddMenu(false);
              props.setSelectedItem && props.setSelectedItem(null);
            }}>
            Cancel
          </Button>
          {/* MAIN-844: keeps the intended status inside the form so the yup
              schema can relax itself for a draft. */}
          <input type="hidden" {...register("status")} />
          <Button
            className="saveDraftBtn"
            disabled={updateLoading || loading || imageUploadLoading}
            variant="outlined"
            color="primary"
            type="submit"
            onClick={() => {
              setImageDeleteCheck(false);
              setValue("status", MENU_ITEM_STATUS.DRAFT);
            }}>
            Save Draft
          </Button>
          <Button
            className="saveBtn"
            disabled={updateLoading || loading || imageUploadLoading}
            variant="contained"
            color="primary"
            type="submit"
            onClick={() => {
              setImageDeleteCheck(false);
              setValue("status", MENU_ITEM_STATUS.PUBLISHED);
            }}>
            {loading || updateLoading ? (
              <CircularProgress color="inherit" size={20} />
            ) : props?.selectedItem === null ? (
              "Add & Publish"
            ) : (
              "Publish"
            )}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
